import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import DropDown from "../../../../components/DropDown";
import InputField from "../../../../components/InputField";
import Icon from "../../../../components/Icon";
import { useEditDetails } from "./EditDetailsData";
import { usePermissions } from "../../../../utils/roleUtils";
import DocDeficiencyModal from "../DocDeficiencyModal";
import DocApproveModal from "../DocApproveModal";
import { getStateFromGST } from "../../../../constants/gstStateCodes";
import { viewDocumentAction } from "../../../../actions/viewDocumentAction";
import { uploadOnboardingDocumentAction } from "../../../../actions/uploadOnboardingDocumentAction";
import { uploadExcelAction } from "../../../../actions/uploadExcelAction";
import { entityLedgerStatusAction } from "../../../../actions/entityLedgerStatusAction";
import { downloadLedgerExcelAction } from "../../../../actions/downloadLedgerExcelAction";
import UtilityForm from "../../../UploadDetails/Components/UtilityForm";
import MSMESection from "../../../UploadDetails/Components/MSMESection";
import ThirdPartySection from "../../../UploadDetails/Components/ThirdPartySection";
import {
  PRIMARY_FIELDS,
  KYC_ENTITY_FIELDS,
  KYC_GST_FIELDS,
  KYC_MSME_FIELDS,
  KYC_CONTACT_FIELDS,
  KYC_BANK_FIELDS,
  DOCS_BY_ENTITY_TYPE,
  DOCS_BANK,
} from "../../AllDataFields";
import "./index.scss";

// ── GST Entries — editable (add/remove rows) or read-only view ───────────────
const GSTEntriesModal = ({
  isGSTRegistered,
  gstEntries,
  onAdd,
  onRemove,
  onChange,
  readOnly,
  documents,
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies,
}) => {
  const isRegistered = isGSTRegistered === true || isGSTRegistered === "yes";
  if (!isRegistered) return null;

  return (
    <div className="gst-entries-modal">
      {gstEntries.map((entry, idx) => {
        // Find the uploaded GST cert for this entry by index key
        const certDoc = documents.find((d) => d.key === `gst_file_${idx}`);
        const certFiles = certDoc?.files?.filter((f) => f.isUploaded) || [];
        const hasCert = certFiles.length > 0;

        return (
          <div key={idx} className="gst-entry-row">
            <div className="gst-entry-row__header">
              <span className="gst-entry-row__index">GST #{idx + 1}</span>
              {!readOnly && gstEntries.length > 1 && (
                <button
                  type="button"
                  className="btn-remove-gst"
                  onClick={() => onRemove(idx)}
                  title="Remove this GST entry"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="gst-entry-row__fields">
              <InputField
                name={`gstNumber_${idx}`}
                label="GST Number"
                type="text"
                placeholder="e.g. 22AAAAA0000A1Z5"
                value={entry.gstNumber || ""}
                maxLength={15}
                onChange={
                  readOnly
                    ? undefined
                    : (e) => onChange(idx, "gstNumber", e.target.value)
                }
                readOnly={readOnly}
              />
              <InputField
                name={`gstState_${idx}`}
                label="State"
                type="text"
                placeholder="Auto-filled from GST number"
                value={entry.gstState || entry.state || ""}
                readOnly
              />
              <InputField
                name={`gstAddress_${idx}`}
                label="GST Address"
                type="text"
                placeholder="Enter GST address"
                value={entry.gstAddress || ""}
                onChange={
                  readOnly
                    ? undefined
                    : (e) => onChange(idx, "gstAddress", e.target.value)
                }
                readOnly={readOnly}
              />

              {/* GST Certificate — 4th column, same row */}
              <div
                className={`doc-slot ${hasCert ? "doc-slot--done" : "doc-slot--pending"}`}
              >
                <div className="doc-slot__header">
                  <span className="doc-slot__icon">{hasCert ? "✓" : "○"}</span>
                  <span className="doc-slot__name">GST Certificate</span>
                  {!hasCert && (
                    <span className="doc-status pending">Pending</span>
                  )}
                </div>
                {hasCert &&
                  certFiles.map((file, fi) => (
                    <div key={fi} className="doc-slot__file">
                      <span>📄 {file.fileName}</span>
                      <div className="doc-slot__actions">
                        <button
                          type="button"
                          className="view-doc-btn"
                          onClick={() => onView(file.filePath)}
                          title="View"
                        >
                          <Icon iconName="view" width={16} height={16} />
                        </button>
                        {canDeficiency && (
                          <button
                            type="button"
                            className="doc-action-btn doc-action-btn--deficiency"
                            onClick={() =>
                              onDocDeficiency &&
                              onDocDeficiency(
                                {
                                  key: `gst_file_${idx}`,
                                  label: "GST Certificate",
                                },
                                file,
                                "",
                              )
                            }
                            title="Mark deficiency"
                          >
                            ✗
                          </button>
                        )}
                        {canApprove && (
                          <button
                            type="button"
                            className="doc-action-btn doc-action-btn--approve"
                            onClick={() =>
                              onDocApprove &&
                              onDocApprove(
                                {
                                  key: `gst_file_${idx}`,
                                  label: "GST Certificate",
                                },
                                file,
                              )
                            }
                            title="Approve"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      })}

      {!readOnly && (
        <button type="button" className="btn-add-gst" onClick={onAdd}>
          + Add GST Entry
        </button>
      )}
    </div>
  );
};
const renderField = (
  field,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
) => {
  const {
    name,
    type,
    label,
    options,
    conditionalOn,
    conditionalValue,
    ...rest
  } = field;

  // Conditional visibility
  if (conditionalOn && entity[conditionalOn] !== conditionalValue) return null;

  const value =
    type === "checkbox" ? entity[name] || false : entity[name] || "";
  const error = errors[name] || "";

  if (type === "select") {
    return (
      <div key={name} className="form-group">
        <label className="input-label" htmlFor={name}>
          {label}
          {rest.required && <span className="required-mark"> *</span>}
        </label>
        <DropDown
          name={name}
          value={value}
          onChange={readOnly ? undefined : handleChange}
          options={options || []}
          error={error}
          fullWidth
          disabled={readOnly}
        />
      </div>
    );
  }

  return (
    <InputField
      key={name}
      name={name}
      label={label}
      type={type || "text"}
      value={value}
      onChange={readOnly ? undefined : handleChange}
      onBlur={readOnly ? undefined : handleBlur}
      error={error}
      readOnly={readOnly && type !== "checkbox"}
      disabled={readOnly && type === "checkbox"}
      {...rest}
    />
  );
};

const renderFields = (
  fields,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
) =>
  fields.map((f) => {
    const el = renderField(
      f,
      entity,
      handleChange,
      handleBlur,
      errors,
      readOnly,
    );
    return el ? React.cloneElement(el, { key: f.name }) : null;
  });

const DocSlot = ({
  doc,
  uploadedFiles = [],
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies = [],
}) => {
  const hasFiles = uploadedFiles.length > 0;
  const [deficiencyTarget, setDeficiencyTarget] = React.useState(null);
  const [approveTarget, setApproveTarget] = React.useState(null);

  // find existing deficiency for a given file
  const getExisting = (file) =>
    docDeficiencies.find((d) => d.docName === (file.fileName || doc.label))
      ?.reason || "";

  return (
    <>
      <div
        className={`doc-slot ${hasFiles ? "doc-slot--done" : "doc-slot--pending"}`}
      >
        <div className="doc-slot__header">
          <span className="doc-slot__icon">{hasFiles ? "✓" : "○"}</span>
          <span className="doc-slot__name">{doc.label}</span>
          {doc.required && <span className="mandatory-badge">Required</span>}
          {!hasFiles && <span className="doc-status pending">Pending</span>}
        </div>

        {hasFiles &&
          uploadedFiles.map((file, i) => {
            const existing = getExisting(file);
            return (
              <div key={i} className="doc-slot__file">
                <span>📄 {file.fileName}</span>
                {file.uploadedOn && (
                  <span className="doc-slot__date">
                    {new Date(file.uploadedOn).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
                <div className="doc-slot__actions">
                  <button
                    type="button"
                    className="view-doc-btn"
                    onClick={() => onView(file.filePath)}
                    title={`View ${file.fileName}`}
                  >
                    <Icon iconName="view" width={16} height={16} />
                  </button>
                  {canDeficiency && (
                    <button
                      type="button"
                      className={`doc-action-btn doc-action-btn--deficiency${existing ? " doc-action-btn--active" : ""}`}
                      onClick={() => setDeficiencyTarget(file)}
                      title={
                        existing
                          ? `Edit deficiency: ${existing}`
                          : "Mark deficiency"
                      }
                    >
                      ✗{existing && <span className="doc-action-btn__dot" />}
                    </button>
                  )}
                  {canApprove && (
                    <button
                      type="button"
                      className="doc-action-btn doc-action-btn--approve"
                      onClick={() => setApproveTarget(file)}
                      title="Approve document"
                    >
                      ✓
                    </button>
                  )}
                </div>
                {existing && (
                  <div className="doc-slot__deficiency-preview">
                    <span className="doc-slot__deficiency-label">
                      Deficiency:
                    </span>
                    <span className="doc-slot__deficiency-text">
                      {existing}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <DocDeficiencyModal
        isOpen={!!deficiencyTarget}
        docName={deficiencyTarget?.fileName || ""}
        existingReason={deficiencyTarget ? getExisting(deficiencyTarget) : ""}
        onClose={() => setDeficiencyTarget(null)}
        onSubmit={(reason) => {
          onDocDeficiency && onDocDeficiency(doc, deficiencyTarget, reason);
          setDeficiencyTarget(null);
        }}
      />
      <DocApproveModal
        isOpen={!!approveTarget}
        docName={approveTarget?.fileName || ""}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => {
          onDocApprove && onDocApprove(doc, approveTarget);
          setApproveTarget(null);
        }}
      />
    </>
  );
};

const DocSection = ({
  title,
  docs,
  documents,
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies,
  entity = {},
  handleChange,
  handleBlur,
  errors = {},
  readOnly = true,
}) => {
  if (!docs || docs.length === 0) return null;
  return (
    <div className="doc-inline-section">
      {title && <p className="doc-inline-section__title">{title}</p>}
      <div className="doc-inline-grid">
        {docs.map((doc) => {
          const matched =
            documents.find((d) => d.key === doc.key) ||
            documents.find((d) =>
              d.documentName
                ?.toLowerCase()
                .includes(doc.label.toLowerCase().split(" ")[0]),
            );
          const uploadedFiles =
            matched?.files?.filter((f) => f.isUploaded) || [];
          const inputVal = doc.inputField
            ? entity[doc.inputField.name] || ""
            : "";

          return (
            <div key={doc.key} className="doc-section-row">
              {/* Input field for the document number — same row as KYC form */}
              {doc.inputField && (
                <InputField
                  name={doc.inputField.name}
                  label={`${doc.label} Number`}
                  type="text"
                  value={inputVal}
                  onChange={readOnly ? undefined : handleChange}
                  onBlur={readOnly ? undefined : handleBlur}
                  placeholder={doc.inputField.placeholder}
                  maxLength={doc.inputField.maxLength}
                  error={errors?.[doc.inputField.name] || ""}
                  readOnly={readOnly}
                />
              )}
              <DocSlot
                doc={doc}
                uploadedFiles={uploadedFiles}
                onView={onView}
                canApprove={canApprove}
                canDeficiency={canDeficiency}
                onDocApprove={onDocApprove}
                onDocDeficiency={onDocDeficiency}
                docDeficiencies={docDeficiencies}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const EditClientModal = ({
  isOpen,
  onClose,
  clientData,
  onSave,
  onApprove,
  onDeficiency,
}) => {
  const {
    formData: entity,
    errors,
    isSubmitting,
    departmentOptions,
    parentOptions,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    showThirdParty,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
  } = useEditDetails(clientData, onSave, onClose);

  const {
    canApprove,
    canDeficiency,
    isReadOnly,
    canEdit,
    canUpdate,
    isFinance,
  } = usePermissions();

  const [isEditing, setIsEditing] = useState(!clientData?.viewOnly && canEdit);

  useEffect(() => {
    setIsEditing(!clientData?.viewOnly && canEdit);
  }, [clientData, canEdit]);

  const effectiveReadOnly = isReadOnly || !isEditing;

  // ── Local GST entries state (seeded from entity.gstList) ─────────────────
  const [localGstEntries, setLocalGstEntries] = useState([]);

  useEffect(() => {
    const list = entity.gstList;
    if (Array.isArray(list) && list.length > 0) {
      setLocalGstEntries(
        list.map((g) => ({
          gstNumber: g.gstNumber || "",
          gstState: g.state || "",
          state: g.state || "",
          gstAddress: g.gstAddress || "",
        })),
      );
    } else if (!effectiveReadOnly) {
      setLocalGstEntries([{ gstNumber: "", gstState: "", gstAddress: "" }]);
    }
  }, [entity.gstList, effectiveReadOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGstAdd = () =>
    setLocalGstEntries((prev) => [
      ...prev,
      { gstNumber: "", gstState: "", gstAddress: "" },
    ]);

  const handleGstRemove = (idx) =>
    setLocalGstEntries((prev) => prev.filter((_, i) => i !== idx));

  const handleGstChange = (idx, field, value) => {
    setLocalGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== idx) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          updated.state = updated.gstState;
        }
        return updated;
      }),
    );
  };

  // saved per-doc deficiencies: [{ docName, reason }]
  const [docDeficiencies, setDocDeficiencies] = useState([]);
  const [deficiencyReviewOpen, setDeficiencyReviewOpen] = useState(false);
  const [approveReviewOpen, setApproveReviewOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  // Tracks if requireKYC was toggled to true in THIS session (not from API)
  const [kycJustToggled, setKycJustToggled] = useState(false);

  // reset when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setDocDeficiencies([]);
      setViewedFilePaths(new Set());
      setApprovedFilePaths(new Set());
      setApproveReviewOpen(false);
      setKycJustToggled(false);
      setLedgerEnabled("no");
      setLedgerName("");
      setMailSending("no");
      setLedgerFile(null);
      setLedgerUploadResponse(null);
      setUploadedPaths({});
    }
  }, [isOpen]);

  // Track which file paths have been viewed
  const [viewedFilePaths, setViewedFilePaths] = useState(new Set());
  // Track which file paths have been individually approved (✓ per doc)
  const [approvedFilePaths, setApprovedFilePaths] = useState(new Set());
  // Newly uploaded doc paths — keyed by documentType, merged into update payload
  const [uploadedPaths, setUploadedPaths] = useState({});

  // Upload a file and store the returned path
  const handleDocUpload = async (documentType, file) => {
    if (!file) return;
    const entityId = entity.entityId || "";
    const result = await uploadOnboardingDocumentAction({
      entityId,
      id: 0,
      documentType,
      fileName: file.name.replace(/\.[^/.]+$/, ""),
      file,
    });
    if (result.success && result.data?.filePath) {
      setUploadedPaths((prev) => ({
        ...prev,
        [documentType]: result.data.filePath,
      }));
      toast.success(`${documentType.replace(/Path$/, "")} uploaded`);
    } else if (!result.success) {
      toast.error(`Upload failed: ${result.error || documentType}`);
    }
  };

  // ── Ledger state (Finance role only) ─────────────────────────────────────
  const [ledgerEnabled, setLedgerEnabled] = useState("no");
  const [ledgerName, setLedgerName] = useState("");
  const [mailSending, setMailSending] = useState("no");
  const [ledgerFile, setLedgerFile] = useState(null);
  const [ledgerSending, setLedgerSending] = useState(false);
  const [ledgerUploadResponse, setLedgerUploadResponse] = useState(null);

  const handleViewDocument = async (filePath) => {
    if (!filePath) return;
    const entityPrimaryId = entity.entityPrimaryId || entity.id || 0;
    const result = await viewDocumentAction(entityPrimaryId, filePath);
    if (result.success) {
      // Mark as viewed only on successful load
      setViewedFilePaths((prev) => new Set([...prev, filePath]));
    } else {
      // Fallback — open raw path directly
      window.open(filePath, "_blank", "noopener,noreferrer");
      setViewedFilePaths((prev) => new Set([...prev, filePath]));
    }
  };

  // called from DocSlot — saves deficiency locally
  const handleDocDeficiency = (doc, file, reason) => {
    // Use the document label (e.g. "PAN Card", "GST Certificate") not the UUID filename
    const displayName = doc.documentName || doc.label || file.fileName || "";
    setDocDeficiencies((prev) => {
      const filtered = prev.filter((d) => d.docName !== displayName);
      return [
        ...filtered,
        {
          docName: displayName,
          fileName: displayName, // send label as fileName to API
          filePath: file.filePath || "",
          reason,
        },
      ];
    });
    toast.info(`Deficiency saved for "${displayName}"`);
  };

  const handleDocApprove = (doc, file) => {
    setApprovedFilePaths((prev) => new Set([...prev, file.filePath]));
    toast.success(`"${file.fileName || doc.label}" approved`);
  };

  // main Deficiency button → open review modal
  const handleDeficiencyButtonClick = () => {
    setDeficiencyReviewOpen(true);
  };

  // from review modal → call API
  const handleDeficiencySubmitToApi = async () => {
    if (!onDeficiency) return;
    if (docDeficiencies.length === 0) {
      toast.error("No deficiencies added. Please mark at least one document.");
      return;
    }
    setIsActionLoading(true);
    try {
      const email = (entity.emailAddress || "").trim().split(/\s+/)[0];
      const spoc = (entity.spocEmailAddress || "").trim().split(/\s+/)[0];

      // Build structured deficiencies array — one entry per document
      const deficiencies = docDeficiencies.map((d) => ({
        fileName: d.fileName || d.docName || "",
        filePath: d.filePath || "",
        descriptions: d.reason || "",
      }));

      await onDeficiency({
        entityPrimaryId: entity.entityPrimaryId || entity.id || 0,
        entityId: entity.entityId || "",
        emailAddress: email,
        spocEmail: spoc,
        deficiencies,
      });

      setDeficiencyReviewOpen(false);
      setDocDeficiencies([]);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleApproveClick = () => {
    // Open the approve review modal — user must approve each doc there
    setApproveReviewOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!onApprove) return;
    setIsActionLoading(true);
    try {
      await onApprove({
        entityPrimaryId: entity.entityPrimaryId || entity.id,
        email: (entity.emailAddress || entity.contactPersonEmail || "")
          .trim()
          .split(/\s+/)[0],
        spocEmail: (entity.spocEmailAddress || "").trim().split(/\s+/)[0],
      });
      setApproveReviewOpen(false);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  if (!clientData) return null;

  const documents = clientData.documents || [];

  // All uploaded file paths — used to gate the Approve button
  const allUploadedFilePaths = documents
    .flatMap((d) => d.files?.filter((f) => f.isUploaded && f.filePath) || [])
    .map((f) => f.filePath);

  // Count docs that have NO uploaded files (pending)
  const pendingDocCount = documents.filter(
    (d) => !d.files?.some((f) => f.isUploaded),
  ).length;

  // Approve enabled only when:
  // 1. There are uploaded docs
  // 2. No pending (not-yet-uploaded) docs
  // 3. Every uploaded file has been viewed AND individually approved
  const allDocsViewed =
    allUploadedFilePaths.length > 0 &&
    allUploadedFilePaths.every((p) => viewedFilePaths.has(p));
  const allDocsApproved =
    allUploadedFilePaths.length > 0 &&
    allUploadedFilePaths.every((p) => approvedFilePaths.has(p));
  const approveReady =
    pendingDocCount === 0 && allDocsViewed && allDocsApproved;

  const resolvedPrimaryFields = PRIMARY_FIELDS.map((f) => {
    if (f.name === "aaDepartmentFunction")
      return { ...f, options: departmentOptions };
    if (f.name === "parentCode") return { ...f, options: parentOptions };
    return f;
  });

  const entityTypeDocs = DOCS_BY_ENTITY_TYPE[entity.entityType] || [];

  const headerAction = canEdit ? (
    <Icon
      iconName={isEditing ? "view" : "edit"}
      className="modal-edit-toggle"
      onClick={() => setIsEditing((prev) => !prev)}
    />
  ) : (
    <button className="modal-close" onClick={handleClose}>
      &times;
    </button>
  );

  const docSectionProps = {
    documents,
    onView: handleViewDocument,
    canApprove,
    canDeficiency,
    onDocApprove: handleDocApprove,
    onDocDeficiency: handleDocDeficiency,
    docDeficiencies,
    entity,
    handleChange: effectiveReadOnly ? undefined : handleChange,
    handleBlur: effectiveReadOnly ? undefined : handleBlur,
    errors,
    readOnly: effectiveReadOnly,
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Entity Details"
        size="large"
        entityID={entity.entityId || entity.entityPrimaryId || entity.id || ""}
        headerAction={headerAction}
      >
        <form className="edit-entity-form">
          <div className="form-content">
            {/* 1 ── Primary Details — always visible */}
            <div className="form-section">
              <h3 className="section-title">Primary Details</h3>
              <div className="form-grid">
                {renderFields(
                  resolvedPrimaryFields,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
              </div>

              {/* Require KYC toggle — always visible, calls update API on change */}
              <div className="require-kyc-row">
                <div
                  className="form-group"
                  style={{ maxWidth: 260, marginTop: 12 }}
                >
                  <label className="input-label">Require KYC</label>
                  <DropDown
                    name="requireKYC"
                    value={entity.requireKYC ? "true" : "false"}
                    onChange={async (e) => {
                      const newVal = e.target.value === "true";
                      const synth = {
                        target: {
                          name: "requireKYC",
                          value: newVal,
                          type: "text",
                        },
                      };
                      handleChange(synth);
                      if (newVal) setKycJustToggled(true);
                      else setKycJustToggled(false);
                      if (onSave) {
                        try {
                          // When toggling to true, send all KYC fields as empty defaults
                          const payload = newVal
                            ? {
                                ...entity,
                                requireKYC: true,
                                tradeName: "",
                                pinCode: "",
                                city: "",
                                State: "",
                                state: "",
                                address: "",
                                natureOfBusiness: "",
                                entityType: "",
                                CompanypanNumber: "",
                                contactPersonName: "",
                                contactPersonDesignation: "",
                                contactPersonMobile: "",
                                isMSMERegistered: "",
                                isRegisteredUnderMSME: false,
                                msmeNumber: "",
                                isGSTRegistered: "",
                                utilityType: "",
                                providerName: "",
                                accountOrConsumerNumber: "",
                                addressOnUtilityBill: "",
                                hasBankDetails: "",
                                bankName: "",
                                accountNumber: "",
                                ifscCode: "",
                                accountType: "",
                                branchName: "",
                                beneficiaryName: "",
                                relationWithBeneficiary: "",
                              }
                            : { ...entity, requireKYC: false };
                          await onSave(payload);
                        } catch {
                          /* toast handled in TableData */
                        }
                      }
                    }}
                    options={[
                      { value: "false", label: "No — KYC not required" },
                      { value: "true", label: "Yes — KYC required" },
                    ]}
                    disabled={effectiveReadOnly || entity.requireKYC === true}
                    fullWidth
                  />
                </div>
                {!entity.requireKYC && (
                  <p
                    className="require-kyc-notice__text"
                    style={{ marginTop: 8 }}
                  >
                    KYC is not required for this entity. Change to "Yes" to view
                    and fill KYC details.
                  </p>
                )}
                {kycJustToggled && entity.requireKYC && (
                  <p
                    className="require-kyc-notice__text"
                    style={{
                      marginTop: 8,
                      color: "#16a34a",
                      background: "#f0fdf4",
                      borderColor: "#bbf7d0",
                    }}
                  >
                    ✓ KYC requirement saved. Close and reopen this record to
                    fill in KYC details.
                  </p>
                )}
              </div>
            </div>

            {/* 2–6 ── KYC sections:
                - Show when requireKYC was true FROM THE API (clientData.entity.requireKYC)
                - Hide when user just toggled it in this session (kycJustToggled)
                  → they need to reopen the modal after saving to see the full form */}
            {entity.requireKYC && !kycJustToggled && (
              <>
                {/* 2 ── KYC Information */}
                <div className="form-section">
                  <h3 className="section-title">KYC Information</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_ENTITY_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                    )}
                  </div>
                  <DocSection
                    title={
                      entity.entityType
                        ? `Documents — ${entity.entityType.replace(/_/g, " ")}`
                        : null
                    }
                    docs={entityTypeDocs}
                    {...docSectionProps}
                  />
                </div>

                {/* 3 ── Contact Person Details */}
                <div className="form-section">
                  <h3 className="section-title">Contact Person Details</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_CONTACT_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                    )}
                  </div>
                </div>

                {/* 4 ── GST Information */}
                <div className="form-section">
                  <h3 className="section-title">GST Information</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_GST_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                    )}
                  </div>

                  {/* GST registered — editable entries or read-only view */}
                  {(entity.isGSTRegistered === true ||
                    entity.isGSTRegistered === "yes") && (
                    <GSTEntriesModal
                      isGSTRegistered={entity.isGSTRegistered}
                      gstEntries={localGstEntries}
                      onAdd={handleGstAdd}
                      onRemove={handleGstRemove}
                      onChange={handleGstChange}
                      readOnly={effectiveReadOnly}
                      documents={documents}
                      onView={handleViewDocument}
                      canApprove={canApprove}
                      canDeficiency={canDeficiency}
                      onDocApprove={handleDocApprove}
                      onDocDeficiency={handleDocDeficiency}
                      docDeficiencies={docDeficiencies}
                    />
                  )}

                  {/* GST not registered — UtilityForm in edit + always show uploaded doc */}
                  {(entity.isGSTRegistered === false ||
                    entity.isGSTRegistered === "no") && (
                    <>
                      {!effectiveReadOnly && (
                        <UtilityForm
                          isGSTRegistered="no"
                          formData={entity}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          utilityFile={utilityFile}
                          handleUtilityFileChange={(file) => {
                            setUtilityFile(file);
                            handleDocUpload("UtilityBillDocumentPath", file);
                          }}
                        />
                      )}
                      <DocSection
                        title="Utility Bill (Address Proof)"
                        docs={[
                          {
                            key: "doc_utility_bill",
                            label: "Utility Bill (Address Proof)",
                            required: true,
                          },
                        ]}
                        {...docSectionProps}
                      />
                    </>
                  )}
                </div>

                {/* 5 ── MSME Information */}
                <div className="form-section">
                  <h3 className="section-title">MSME Information</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_MSME_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                    )}
                  </div>
                  {/* MSMESection in edit mode, DocSection in view mode */}
                  {!effectiveReadOnly ? (
                    <MSMESection
                      isMSMERegistered={entity.isMSMERegistered}
                      formData={entity}
                      errors={errors}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      msmeFile={msmeFile}
                      handleMsmeFileChange={(file) => {
                        setMsmeFile(file);
                        handleDocUpload("MSMECertificatePath", file);
                      }}
                    />
                  ) : (
                    <DocSection
                      title="MSME Documents"
                      docs={[
                        {
                          key: "doc_msme_certificate",
                          label: "MSME Registration Certificate",
                          required: true,
                          inputField: {
                            name: "msmeNumber",
                            placeholder: "MSME Registration Number",
                            maxLength: 19,
                          },
                        },
                      ]}
                      {...docSectionProps}
                    />
                  )}
                </div>

                {/* 6 ── Bank Details */}
                <div className="form-section">
                  <h3 className="section-title">Bank Details</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_BANK_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                    )}
                    {entity.thirdPartyPayment && (
                      <InputField
                        name="relationWithBeneficiary"
                        label="Relation with Beneficiary"
                        value={entity.relationWithBeneficiary || ""}
                        onChange={effectiveReadOnly ? undefined : handleChange}
                        fullWidth
                        readOnly={effectiveReadOnly}
                      />
                    )}
                  </div>
                  <DocSection
                    title="Bank Documents"
                    docs={DOCS_BANK}
                    {...docSectionProps}
                  />
                  {/* ThirdPartySection in edit mode, DocSection in view mode */}
                  {showThirdParty &&
                    (!effectiveReadOnly ? (
                      <ThirdPartySection
                        show={true}
                        formData={entity}
                        errors={errors}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        thirdPartyFile={thirdPartyFile}
                        handleThirdPartyFileChange={(file) => {
                          setThirdPartyFile(file);
                          handleDocUpload("UploadSignedFormPath", file);
                        }}
                      />
                    ) : (
                      <DocSection
                        title="Signed Form (Third Party)"
                        docs={[
                          {
                            key: "third_party_file",
                            label: "Signed Form (Third Party)",
                            required: true,
                          },
                        ]}
                        {...docSectionProps}
                      />
                    ))}
                </div>
                {/* ── Finance: Ledger section ── */}
                {isFinance && (
                  <div className="ledger-panel">
                    <div className="ledger-panel__header">
                      <span className="ledger-panel__title">📋 Ledger</span>
                    </div>

                    {/* Row 1: all fields */}
                    <div className="ledger-panel__row">
                      {/* Col 1: Ledger Name */}
                      <div className="ledger-panel__field">
                        <label className="ledger-panel__label">
                          Ledger Name
                        </label>
                        <input
                          className="ledger-panel__input"
                          type="text"
                          placeholder="Enter ledger name"
                          value={ledgerName}
                          onChange={(e) => setLedgerName(e.target.value)}
                        />
                      </div>

                      {/* Col 2: Ledger Yes/No/Blank */}
                      <div className="ledger-panel__field">
                        <label className="ledger-panel__label">Ledger</label>
                        <select
                          className="ledger-panel__select"
                          value={ledgerEnabled}
                          onChange={(e) => {
                            setLedgerEnabled(e.target.value);
                            if (e.target.value !== "yes") {
                              setMailSending("no");
                              setLedgerFile(null);
                              setLedgerUploadResponse(null);
                            }
                          }}
                        >
                          <option value="">— Select —</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="blank">Blank</option>
                        </select>
                      </div>

                      {/* Col 3: Send Mail */}
                      <div className="ledger-panel__field">
                        <label className="ledger-panel__label">Send Mail</label>
                        <select
                          className="ledger-panel__select"
                          value={mailSending}
                          disabled={ledgerEnabled !== "yes"}
                          onChange={(e) => setMailSending(e.target.value)}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes — send to entity</option>
                        </select>
                      </div>

                      {/* Col 4: Document upload */}
                      <div className="ledger-panel__field ledger-panel__field--file">
                        <label className="ledger-panel__label">Document</label>
                        <label
                          className={`ledger-panel__file-btn${ledgerEnabled !== "yes" ? " ledger-panel__file-btn--disabled" : ""}`}
                        >
                          <input
                            type="file"
                            accept=".xlsx,.xls,.pdf"
                            style={{ display: "none" }}
                            disabled={ledgerEnabled !== "yes"}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setLedgerFile(file);
                              setLedgerUploadResponse(null);
                              const result = await uploadExcelAction({
                                entityId: entity.entityId || "",
                                file,
                              });
                              if (result.success) {
                                setLedgerUploadResponse(result.data);
                                toast.success("File uploaded successfully");
                              } else {
                                toast.error(
                                  result.error || "File upload failed",
                                );
                                setLedgerFile(null);
                              }
                            }}
                          />
                          {ledgerFile ? (
                            <span className="ledger-panel__file-name">
                              📎 {ledgerFile.name}
                              {ledgerUploadResponse && (
                                <span className="ledger-panel__ok"> ✓</span>
                              )}
                            </span>
                          ) : (
                            <span className="ledger-panel__file-placeholder">
                              Choose file (Excel / PDF)
                            </span>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Row 2: action buttons — right aligned */}
                    <div className="ledger-panel__actions">
                      {/* {ledgerUploadResponse && ( */}
                      <button
                        type="button"
                        className="ledger-panel__btn ledger-panel__btn--download"
                        onClick={async () => {
                          const result = await downloadLedgerExcelAction(
                            ledgerUploadResponse.filePath,
                            ledgerUploadResponse.fileName,
                          );
                          if (!result.success)
                            toast.error(result.error || "Download failed");
                        }}
                      >
                        ⬇ Download Uploaded File
                      </button>
                      {/* )} */}
                      <button
                        type="button"
                        className="ledger-panel__btn ledger-panel__btn--submit"
                        disabled={
                          ledgerEnabled !== "yes" ||
                          !ledgerUploadResponse ||
                          ledgerSending
                        }
                        onClick={async () => {
                          if (!ledgerUploadResponse) return;
                          setLedgerSending(true);
                          try {
                            const result = await entityLedgerStatusAction({
                              entityPrimaryId:
                                entity.entityPrimaryId || entity.id,
                              entityId: entity.entityId || "",
                              isLedger: true,
                              isLedgerSendByEmail: mailSending === "yes",
                              fileName: ledgerUploadResponse.fileName || "",
                              filePath: ledgerUploadResponse.filePath || "",
                            });
                            if (result.success) {
                              toast.success(
                                mailSending === "yes"
                                  ? "Ledger submitted and email sent!"
                                  : "Ledger submitted successfully!",
                              );
                              setLedgerFile(null);
                              setLedgerEnabled("no");
                              setLedgerName("");
                              setMailSending("no");
                              setLedgerUploadResponse(null);
                            } else {
                              toast.error(
                                result.message || "Failed to submit ledger",
                              );
                            }
                          } finally {
                            setLedgerSending(false);
                          }
                        }}
                      >
                        {ledgerSending
                          ? "Submitting…"
                          : mailSending === "yes"
                            ? "Submit & Send Email"
                            : "Submit Ledger"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {canUpdate && (
              <button
                type="button"
                className="btn-submit"
                disabled={
                  !isEditing || isSubmitting || entity.isVerified === true
                }
                onClick={(e) => handleUpdate(e, uploadedPaths)}
                title={
                  entity.isVerified === true
                    ? "Entity already approved — cannot update"
                    : undefined
                }
              >
                {isSubmitting ? "Updating..." : "Update"}
              </button>
            )}
            {canDeficiency && (
              <button
                type="button"
                className="btn-submit-reject"
                disabled={
                  isSubmitting || isActionLoading || entity.isVerified === true
                }
                onClick={handleDeficiencyButtonClick}
                title={
                  entity.isVerified === true
                    ? "Entity already approved — cannot mark deficiency"
                    : undefined
                }
              >
                {docDeficiencies.length > 0
                  ? `Deficiency (${docDeficiencies.length})`
                  : "Deficiency"}
              </button>
            )}

            {/* isVerified is true then its disblae otherwise enable   */}
            {canApprove && (
              <button
                type="button"
                className="btn-submit-approve"
                disabled={
                  isSubmitting || isActionLoading || entity.isVerified === true
                }
                onClick={handleApproveClick}
                title={
                  entity.isVerified === true
                    ? "Already approved"
                    : "Review and approve documents"
                }
              >
                {isActionLoading
                  ? "Approving..."
                  : entity.isVerified === true
                    ? "Approved"
                    : "Approve"}
              </button>
            )}
          </div>
        </form>
      </Modal>

      {/* Deficiency Review Modal — editable list, then submits to API */}
      <Modal
        isOpen={deficiencyReviewOpen}
        onClose={() => setDeficiencyReviewOpen(false)}
        title="Submit Deficiency"
        size="small"
      >
        <div className="doc-deficiency-review">
          {docDeficiencies.length === 0 ? (
            <div className="doc-deficiency-review__empty">
              <span className="doc-deficiency-review__empty-icon">⚠️</span>
              <p>No deficiencies added yet.</p>
              <p className="doc-deficiency-review__empty-hint">
                Close this and click ✗ on individual documents first.
              </p>
            </div>
          ) : (
            <>
              <p className="doc-deficiency-review__hint">
                Review and edit before submitting — {docDeficiencies.length}{" "}
                document{docDeficiencies.length > 1 ? "s" : ""}
              </p>
              <ul className="doc-deficiency-review__list">
                {docDeficiencies.map((d, i) => (
                  <li key={i} className="doc-deficiency-review__item">
                    <div className="doc-deficiency-review__item-header">
                      <span className="doc-deficiency-review__item-doc">
                        📄 {d.docName}
                      </span>
                      <button
                        type="button"
                        className="doc-deficiency-review__item-remove"
                        onClick={() =>
                          setDocDeficiencies((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                    <textarea
                      className="doc-deficiency-review__item-textarea"
                      rows={2}
                      value={d.reason}
                      onChange={(e) =>
                        setDocDeficiencies((prev) =>
                          prev.map((item, idx) =>
                            idx === i
                              ? { ...item, reason: e.target.value }
                              : item,
                          ),
                        )
                      }
                      placeholder="Edit reason..."
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="doc-deficiency-review__actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setDeficiencyReviewOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit-reject"
              disabled={
                docDeficiencies.length === 0 ||
                docDeficiencies.some((d) => !d.reason.trim()) ||
                isActionLoading
              }
              onClick={handleDeficiencySubmitToApi}
            >
              {isActionLoading ? "Submitting..." : "Submit Deficiency"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Approve Review Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={approveReviewOpen}
        onClose={() => setApproveReviewOpen(false)}
        title="Review & Approve Documents"
        size="medium"
      >
        <div className="approve-review">
          {/* Progress header */}
          <div className="approve-review__progress-header">
            <span className="approve-review__progress-header-title">
              Document Review
            </span>
            <span className="approve-review__progress-header-count">
              {approvedFilePaths.size} / {allUploadedFilePaths.length} approved
              {pendingDocCount > 0 && ` · ${pendingDocCount} pending`}
            </span>
          </div>
          {allUploadedFilePaths.length > 0 && (
            <div className="approve-review__progress-bar">
              <div
                className="approve-review__progress-bar-fill"
                style={{
                  width: `${(approvedFilePaths.size / allUploadedFilePaths.length) * 100}%`,
                }}
              />
            </div>
          )}
          <p className="approve-review__hint">
            View each uploaded document then approve it one by one. All
            documents must be uploaded and approved to confirm.
          </p>

          {/* Full document list — uploaded + pending */}
          <ul className="approve-review__list">
            {documents.map((doc) => {
              const uploadedFiles =
                doc.files?.filter((f) => f.isUploaded && f.filePath) || [];
              const isPending = uploadedFiles.length === 0;

              if (isPending) {
                return (
                  <li key={doc.key} className="approve-review__item">
                    <div
                      className="approve-review__item-dot"
                      style={{ background: "#fef3c7", color: "#d97706" }}
                    >
                      ⚠
                    </div>
                    <div className="approve-review__item-info">
                      <span className="approve-review__item-name">
                        {doc.documentName}
                      </span>
                      <span
                        className="approve-review__item-file"
                        style={{ color: "#d97706" }}
                      >
                        Pending upload
                      </span>
                    </div>
                    <div className="approve-review__item-actions">
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#d97706",
                          fontWeight: 600,
                        }}
                      >
                        Not uploaded
                      </span>
                    </div>
                  </li>
                );
              }

              return uploadedFiles.map((file, fi) => {
                const isApproved = approvedFilePaths.has(file.filePath);
                const isViewed = viewedFilePaths.has(file.filePath);
                return (
                  <li
                    key={`${doc.key}-${fi}`}
                    className={`approve-review__item ${isApproved ? "approve-review__item--approved" : ""}`}
                  >
                    <div
                      className={`approve-review__item-dot ${isApproved ? "approve-review__item-dot--approved" : ""}`}
                    >
                      {isApproved ? "✓" : fi + 1}
                    </div>
                    <div className="approve-review__item-info">
                      <span className="approve-review__item-name">
                        {doc.documentName}
                      </span>
                      <span className="approve-review__item-file">
                        📄 {file.fileName}
                      </span>
                    </div>
                    <div className="approve-review__item-actions">
                      <button
                        type="button"
                        className={`view-doc-btn ${isViewed ? "view-doc-btn--viewed" : ""}`}
                        onClick={() => handleViewDocument(file.filePath)}
                        title={isViewed ? "Already viewed" : "View document"}
                      >
                        <Icon iconName="view" width={13} height={13} />
                        {isViewed ? "Viewed" : "View"}
                      </button>
                      {isApproved ? (
                        <span className="approve-review__item-approved-badge">
                          ✓ Approved
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="doc-action-btn doc-action-btn--approve"
                          disabled={!isViewed}
                          title={
                            isViewed
                              ? "Approve this document"
                              : "View document first"
                          }
                          onClick={() => handleDocApprove(doc, file)}
                        >
                          ✓ Approve
                        </button>
                      )}
                    </div>
                  </li>
                );
              });
            })}
          </ul>

          {/* Footer */}
          <div className="approve-review__actions">
            <span className="approve-review__actions-status">
              {approveReady ? (
                <strong>All documents approved — ready to submit</strong>
              ) : pendingDocCount > 0 ? (
                `${pendingDocCount} document(s) still pending upload`
              ) : !allDocsViewed ? (
                `View all documents first (${viewedFilePaths.size}/${allUploadedFilePaths.length} viewed)`
              ) : (
                `Approve each document (${approvedFilePaths.size}/${allUploadedFilePaths.length} approved)`
              )}
            </span>
            <div className="approve-review__actions-btns">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setApproveReviewOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit-approve"
                disabled={!approveReady || isActionLoading}
                onClick={handleApproveConfirm}
              >
                {isActionLoading ? "Approving..." : "Confirm Approve"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditClientModal;
